import { LatLng, TripLegMode } from "types/map";
import { CarState } from "types/cars";
import {
  flightEmissionKg,
  greatCircleDistanceKm,
} from "libraries/flight";
import { ElectricGridCountry, tripEmission } from "libraries/emission";

/** French average rail ~12.8 g CO₂e / passenger-km (ADEME-style placeholder). */
export const TRAIN_KG_CO2_PER_KM = 0.0128;

/** Typical rail path vs great-circle when Transit directions are unavailable. */
export const TRAIN_DETOUR_FACTOR = 1.2;

export type HopEmissionOptions = {
  routedKm?: number;
  car?: CarState | null;
  gridCountry?: ElectricGridCountry;
};

export const trainDistanceKm = (from: LatLng, to: LatLng): number =>
  greatCircleDistanceKm(from, to) * TRAIN_DETOUR_FACTOR;

export const trainEmissionKg = (from: LatLng, to: LatLng): number =>
  trainDistanceKm(from, to) * TRAIN_KG_CO2_PER_KM;

/** Distance in meters for a hop when Directions routing is unavailable. */
export const fallbackHopMeters = (
  mode: TripLegMode,
  from: LatLng,
  to: LatLng
): number => {
  if (mode === "train") {
    return trainDistanceKm(from, to) * 1000;
  }
  if (mode === "fly") {
    return greatCircleDistanceKm(from, to) * 1000;
  }
  return greatCircleDistanceKm(from, to) * 1000;
};

/**
 * Hop CO₂ in kg.
 * Walk/bike: negative = CO₂ avoided vs driving the same distance with the selected car.
 * Train/fly: positive operational emissions.
 * Drive: undefined (handled by trip driving total).
 */
export const hopEmissionKg = (
  mode: TripLegMode,
  from: LatLng,
  to: LatLng,
  options: HopEmissionOptions = {}
): number | undefined => {
  const { routedKm, car = null, gridCountry = "norway" } = options;

  if (mode === "drive") {
    return undefined;
  }

  if (mode === "fly") {
    return flightEmissionKg(from, to);
  }

  if (mode === "train") {
    const km = routedKm ?? trainDistanceKm(from, to);
    return km * TRAIN_KG_CO2_PER_KM;
  }

  if (mode === "walk" || mode === "bike") {
    const meters =
      routedKm !== undefined
        ? routedKm * 1000
        : greatCircleDistanceKm(from, to) * 1000;
    const { totalKgEmission } = tripEmission(car, meters, { gridCountry });
    return -totalKgEmission;
  }

  return undefined;
};

/** Avoided CO₂ (negative kg) for walk+bike distance vs the selected car. */
export const avoidedCarEmissionKg = (
  distanceMeters: number,
  car: CarState | null,
  gridCountry: ElectricGridCountry = "norway"
): number => {
  if (distanceMeters <= 0) {
    return 0;
  }
  const { totalKgEmission } = tripEmission(car, distanceMeters, {
    gridCountry,
  });
  return -totalKgEmission;
};
